import { executeFinanceCommand } from "@/features/finance/lib/retry-safe-command";
import type {
  ApplyCreditPayload,
  CreditApplication,
  CreditApplicationMutationResult,
  CreditBalance,
  CustomerCredit,
  Estimate,
  EstimateFilters,
  Invoice,
  ReverseCreditPayload,
} from "@/features/credits/types";
import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import {
  buildAppliedAmountMap,
  collectReversedApplicationIds,
  markReversedApplications,
  sumAppliedAmount,
} from "@/lib/credit-application-balances";
import { orderListPage, tiebreakListOrder } from "@/lib/db/list-order";
import { readAllPages } from "@/lib/db/paged-read";
import { roundToCents } from "@/lib/money";
import {
  cast,
  firstRecord,
  getOptionalString,
  isRecord,
  toNumber,
  toPostgrestDayUntil,
  toPostgrestIlikeContains,
} from "@/lib/utils/query-helpers";

const dataClient = getBackendClient().data;

const INVOICE_SELECT = `
  id, uid, date, notes, total_amount, total_paid, balance, payment_status,
  account_id, created_at, pdf_url,
  gl_accounts!gl_invoices_account_id_fkey ( id, name, uid )
`;

const ESTIMATE_SELECT = `
  id, uid, date, notes, total_amount, total_paid, balance, payment_status,
  is_converted, is_sample, is_quick, detail_completed_at, project_id,
  converted_date, converted_invoice_id, account_id, created_at,
  gl_accounts!gl_estimates_account_id_fkey ( id, name, uid )
`;

const CREDIT_SELECT = `
  id, uid, amount, date, method, reference, account_id, estimate_id,
  gl_accounts!gl_customer_credits_account_id_fkey ( id, name, uid )
`;

const CREDIT_BALANCE_CREDIT_SELECT = `
  id, amount, account_id,
  gl_accounts!gl_customer_credits_account_id_fkey ( id, name, uid )
`;

const CREDIT_APPLICATION_SELECT = `
  id, credit_id, estimate_id, invoice_id, amount_applied, is_reversal,
  applied_at, reverses_application_id,
  gl_invoices!gl_credit_applications_invoice_id_fkey ( id, uid ),
  gl_estimates!gl_credit_applications_estimate_id_fkey ( id, uid )
`;

const REVERSAL_LOOKUP_CHUNK_SIZE = 50;

interface CreditBalanceRow {
  account_id: string | null;
  // `numeric` columns can arrive as strings over PostgREST; the read below
  // coerces with `toNumber`, so the type admits the string it can carry.
  amount: number | string | null;
  gl_accounts: Record<string, unknown> | Record<string, unknown>[] | null | undefined;
  id: string;
}

function getRecordString(value: Record<string, unknown>, key: string): string {
  const candidate = value[key];
  return typeof candidate === "string" ? candidate : "";
}

function getPositiveLimit(limit: number | undefined): number {
  const normalized = Number.parseInt(String(limit ?? 50), 10);
  return normalized;
}

function validatePositiveLimit(limit: number): string | null {
  if (!Number.isInteger(limit) || limit <= 0) {
    return "limit must be a positive integer.";
  }
  return null;
}

/**
 * Reads every row an account balance is derived from, one page at a time.
 *
 * The two reads behind `getCreditBalances` are netted into one figure per
 * account, so a capped read is not a short list — it is a wrong number.
 * Truncating `gl_credit_applications` while the credits read is complete
 * subtracts less than has actually been applied and overstates the credit the
 * account still has to spend, with nothing on screen to say so.
 */
async function readAllBalanceRows<TRow>(buildQuery: () => QueryBuilder<TRow>): Promise<TRow[]> {
  return readAllPages(async ({ limit, offset }) => {
    const { data, error } = await buildQuery().range(offset, offset + limit - 1);
    if (error) {
      throw new Error(error.message);
    }
    return data ?? [];
  });
}

/** Lists invoices with optional payment-status filtering and pagination. */
export async function getInvoices(params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<Invoice[]> {
  const limit = params?.limit ?? 50;
  const offset = Math.max(0, params?.offset ?? 0);

  let query = orderListPage(
    dataClient.from("gl_invoices").select(INVOICE_SELECT).is("voided_at", null),
    { ascending: false, column: "date" },
  ).range(offset, offset + limit - 1);

  if (params?.status) {
    query = query.eq("payment_status", params.status);
  }

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }
  return cast<Invoice[]>(data || []);
}

interface GetEstimatesParams {
  account_id?: EstimateFilters["account_id"];
  date_from?: EstimateFilters["date_from"];
  date_to?: EstimateFilters["date_to"];
  is_converted?: EstimateFilters["is_converted"];
  is_quick?: EstimateFilters["is_quick"];
  is_sample?: EstimateFilters["is_sample"];
  limit?: EstimateFilters["limit"];
  offset?: EstimateFilters["offset"];
  search?: EstimateFilters["search"];
  status?: EstimateFilters["status"];
}

function applyEstimateEqualityFilters<T>(
  query: QueryBuilder<T>,
  params: GetEstimatesParams,
): QueryBuilder<T> {
  const filters: [string, unknown][] = [];
  if (params.status) {
    filters.push(["payment_status", params.status]);
  }
  if (params.account_id) {
    filters.push(["account_id", params.account_id]);
  }
  if (params.is_sample !== undefined) {
    filters.push(["is_sample", params.is_sample]);
  }
  if (params.is_quick !== undefined) {
    filters.push(["is_quick", params.is_quick]);
  }
  return filters.reduce((next, [column, value]) => next.eq(column, value), query);
}

function applyEstimateRangeAndConversionFilters<T>(
  query: QueryBuilder<T>,
  params: GetEstimatesParams,
): QueryBuilder<T> {
  let next = query;
  if (params.date_from) {
    next = next.gte("date", params.date_from.split("T")[0]);
  }
  if (params.date_to) {
    next = next.lt("date", toPostgrestDayUntil(params.date_to));
  }
  // Conversion state derives from the authority column `converted_invoice_id`
  // (migrations/20260811130000); the trigger-maintained `is_converted` mirror
  // stays in the wide select but no longer drives filtering.
  if (params.is_converted === true) {
    next = next.not("converted_invoice_id", "is", null);
  } else if (params.is_converted === false) {
    next = next.is("converted_invoice_id", null);
  }
  return next;
}

async function buildEstimateSearchOrFilter(rawSearch: string): Promise<string> {
  const search = toPostgrestIlikeContains(rawSearch);
  const { data: accountMatches, error: accountSearchError } = await dataClient
    .from("gl_accounts")
    .select("id")
    .or(`name.ilike.${search},uid.ilike.${search}`)
    .limit(100);

  if (accountSearchError) {
    throw new Error(accountSearchError.message);
  }

  const accountIds = (accountMatches || []).map((account) => account.id).filter(Boolean);
  let orQuery = `uid.ilike.${search},notes.ilike.${search}`;
  if (accountIds.length > 0) {
    orQuery += `,account_id.in.(${accountIds.join(",")})`;
  }

  return orQuery;
}

/** Lists estimates with normalized date filters, account-name search, and conversion state derived from `converted_invoice_id`. */
export async function getEstimates(params: GetEstimatesParams = {}): Promise<Estimate[]> {
  const limit = params.limit ?? 50;
  const offset = Math.max(0, params.offset ?? 0);

  let query = orderListPage(dataClient.from("gl_estimates").select(ESTIMATE_SELECT), {
    ascending: false,
    column: "date",
  }).range(offset, offset + limit - 1);

  query = applyEstimateEqualityFilters(query, params);
  query = applyEstimateRangeAndConversionFilters(query, params);

  const search = params.search?.trim();
  if (search) {
    query = query.or(await buildEstimateSearchOrFilter(search));
  }

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }
  return cast<Estimate[]>(data || []);
}

/** Computes available customer credit by account from credits and application totals. */
export async function getCreditBalances(): Promise<CreditBalance[]> {
  const [creditRows, applicationRows] = await Promise.all([
    readAllBalanceRows(() =>
      tiebreakListOrder(
        dataClient.from("gl_customer_credits").select(CREDIT_BALANCE_CREDIT_SELECT),
      ),
    ),
    readAllBalanceRows(() =>
      tiebreakListOrder(
        dataClient.from("gl_credit_applications").select("credit_id, amount_applied, is_reversal"),
      ),
    ),
  ]);

  const appliedByCredit = buildAppliedAmountMap(applicationRows, "credit_id");

  const accountMap = new Map<
    string,
    {
      account_id: string;
      account_name: string | null;
      account_uid: string;
      total_credited: number;
      total_applied: number;
    }
  >();

  for (const credit of cast<CreditBalanceRow[]>(creditRows)) {
    if (!credit.account_id) {
      continue;
    }

    const accountRaw = Array.isArray(credit.gl_accounts)
      ? credit.gl_accounts[0]
      : credit.gl_accounts;
    const account = isRecord(accountRaw) ? accountRaw : null;

    if (!accountMap.has(credit.account_id)) {
      accountMap.set(credit.account_id, {
        account_id: credit.account_id,
        account_name: account ? getOptionalString(account, "name") : null,
        account_uid: account ? getRecordString(account, "uid") : "",
        total_credited: 0,
        total_applied: 0,
      });
    }

    const entry = accountMap.get(credit.account_id);
    if (entry) {
      // `numeric` columns can arrive as strings over PostgREST, and the applied
      // side below already coerces. `|| 0` instead concatenates ("0" + "100.00")
      // into a non-finite string that rounds to 0, netting a real application
      // against no credit and reporting available credit as negative — an account
      // holding credit reads as owing it back.
      entry.total_credited = roundToCents(entry.total_credited + (toNumber(credit.amount) ?? 0));
      entry.total_applied = roundToCents(
        entry.total_applied + (appliedByCredit.get(credit.id) || 0),
      );
    }
  }

  return Array.from(accountMap.values()).map((entry) => ({
    ...entry,
    available_credit: roundToCents(entry.total_credited - entry.total_applied),
  }));
}

/** Lists customer credits, optionally scoped to an account, with available balances attached. */
export async function getCustomerCredits(params?: {
  account_id?: string;
  limit?: number;
}): Promise<CustomerCredit[]> {
  const limit = getPositiveLimit(params?.limit);
  const limitError = validatePositiveLimit(limit);
  if (limitError) {
    throw new Error(limitError);
  }

  let query = dataClient
    .from("gl_customer_credits")
    .select(CREDIT_SELECT)
    .order("date", { ascending: false })
    .limit(limit);

  if (params?.account_id) {
    query = query.eq("account_id", params.account_id);
  }

  const { data: credits, error: creditsError } = await query;
  if (creditsError) {
    throw new Error(creditsError.message);
  }

  const creditIds = (credits || []).map((credit) => credit.id);
  let applications: {
    credit_id: string | null;
    amount_applied: number | null;
    is_reversal: boolean | null;
  }[] = [];

  if (creditIds.length > 0) {
    const { data: apps, error: appsError } = await dataClient
      .from("gl_credit_applications")
      .select("credit_id, amount_applied, is_reversal")
      .in("credit_id", creditIds);
    if (appsError) {
      throw new Error(appsError.message);
    }
    applications = apps || [];
  }

  const appliedByCredit = buildAppliedAmountMap(applications, "credit_id");

  return cast<CustomerCredit[]>(
    (credits || []).map((credit) => ({
      ...credit,
      available_balance: roundToCents((credit.amount || 0) - (appliedByCredit.get(credit.id) || 0)),
    })),
  );
}

/** Loads one customer credit with its applications and available balance. */
export async function getCustomerCreditById(
  creditId: string,
): Promise<CustomerCredit & { applications: CreditApplication[] }> {
  const [creditRes, appsRes] = await Promise.all([
    dataClient
      .from("gl_customer_credits")
      .select(
        `id, uid, amount, date, method, reference, account_id, estimate_id, created_at,
         gl_accounts!gl_customer_credits_account_id_fkey ( id, name, uid )`,
      )
      .eq("id", creditId)
      .single(),
    dataClient
      .from("gl_credit_applications")
      .select(CREDIT_APPLICATION_SELECT)
      .eq("credit_id", creditId)
      .order("applied_at", { ascending: false }),
  ]);

  if (creditRes.error) {
    throw new Error(creditRes.error.message);
  }
  if (appsRes.error) {
    throw new Error(appsRes.error.message);
  }

  const applications = cast<CreditApplication[]>(appsRes.data || []);

  const totalApplied = sumAppliedAmount(applications);

  const credit = cast<CustomerCredit>(creditRes.data);
  const available_balance = roundToCents((credit.amount || 0) - totalApplied);

  // No extra read here: this query is unpaged and scoped to one credit, so it
  // already carries every reversal against these applications. The reverse
  // affordance keys off the resulting `is_reversed` flag rather than relying on
  // the RPC to reject a duplicate.
  return {
    ...credit,
    available_balance,
    applications: markReversedApplications(
      applications,
      collectReversedApplicationIds(applications),
    ),
  };
}

/**
 * Resolves which of `applicationIds` already have a reversal against them.
 *
 * Filtered by the ids under consideration rather than listing every reversal:
 * the result is bounded by the caller's page size, so it cannot be cut short by
 * a server-side row cap the way an unfiltered list read could be.
 */
async function fetchReversedApplicationIds(
  applicationIds: readonly string[],
): Promise<Set<string>> {
  if (applicationIds.length === 0) {
    return new Set<string>();
  }

  const reversedApplicationIds = new Set<string>();

  for (let offset = 0; offset < applicationIds.length; offset += REVERSAL_LOOKUP_CHUNK_SIZE) {
    const applicationIdChunk = applicationIds.slice(offset, offset + REVERSAL_LOOKUP_CHUNK_SIZE);
    const { data, error } = await dataClient
      .from("gl_credit_applications")
      .select("reverses_application_id")
      .eq("is_reversal", true)
      .in("reverses_application_id", applicationIdChunk);

    if (error) {
      // Deliberately not swallowed: an empty set would read as "nothing is
      // reversed" and re-enable the reverse action on rows the RPC will reject.
      throw new Error(error.message);
    }

    for (const id of collectReversedApplicationIds(
      cast<{ reverses_application_id: string | null }[]>(data || []),
    )) {
      reversedApplicationIds.add(id);
    }
  }

  return reversedApplicationIds;
}

/** Lists credit application ledger rows with optional credit or account filtering. */
export async function getCreditApplications(params?: {
  credit_id?: string;
  account_id?: string;
  limit?: number;
}): Promise<CreditApplication[]> {
  const limit = getPositiveLimit(params?.limit);
  const limitError = validatePositiveLimit(limit);
  if (limitError) {
    throw new Error(limitError);
  }

  let query = dataClient
    .from("gl_credit_applications")
    .select(CREDIT_APPLICATION_SELECT)
    .order("applied_at", { ascending: false })
    .limit(limit);

  if (params?.credit_id) {
    query = query.eq("credit_id", params.credit_id);
  }

  if (params?.account_id) {
    const { data: accountCredits, error: accountCreditsError } = await dataClient
      .from("gl_customer_credits")
      .select("id")
      .eq("account_id", params.account_id);

    if (accountCreditsError) {
      throw new Error(accountCreditsError.message);
    }

    const accountCreditIds = (accountCredits || []).map((credit) => credit.id);
    if (accountCreditIds.length === 0) {
      return [];
    }
    query = query.in("credit_id", accountCreditIds);
  }

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }

  const applications = cast<CreditApplication[]>(data || []);

  // Second read rather than a page-local scan: this list is paged, so a
  // reversal can be absent from it while the application it reverses is
  // present. One extra query regardless of row count, keyed on the page's ids.
  const reversedApplicationIds = await fetchReversedApplicationIds(
    applications.map((application) => application.id),
  );

  return markReversedApplications(applications, reversedApplicationIds);
}

/** Applies a customer credit to one invoice or estimate through the database RPC. */
export async function applyCredit(
  payload: ApplyCreditPayload,
): Promise<CreditApplicationMutationResult> {
  if (!payload.credit_id) {
    throw new Error("credit_id is required");
  }
  if (!(payload.amount > 0)) {
    throw new Error("Amount must be positive");
  }

  const hasInvoiceTarget = typeof payload.invoice_id === "string" && payload.invoice_id.length > 0;
  const hasEstimateTarget =
    typeof payload.estimate_id === "string" && payload.estimate_id.length > 0;

  if (hasInvoiceTarget === hasEstimateTarget) {
    throw new Error("Provide exactly one of invoice_id or estimate_id.");
  }

  const command = await executeFinanceCommand("customer_credit_application", {
    credit_id: payload.credit_id,
    invoice_id: payload.invoice_id || null,
    estimate_id: payload.estimate_id || null,
    amount: payload.amount,
    applied_at: null,
    reference: null,
  });

  const rpcResult = command.result;
  const rpcRecord = firstRecord(rpcResult);
  const applicationId =
    rpcRecord && typeof rpcRecord.application_id === "string" ? rpcRecord.application_id : null;
  const creditRemaining = rpcRecord ? toNumber(rpcRecord.credit_remaining) : null;

  if (!applicationId) {
    command.acknowledge();
    return cast<CreditApplicationMutationResult>(rpcResult ?? {});
  }

  const { data, error } = await dataClient
    .from("gl_credit_applications")
    .select(CREDIT_APPLICATION_SELECT)
    .eq("id", applicationId)
    .single();

  if (error || !data) {
    command.acknowledge();
    return cast<CreditApplicationMutationResult>(rpcResult ?? {});
  }

  command.acknowledge();
  return {
    ...cast<CreditApplication>(data),
    available_balance: creditRemaining,
    credit_remaining: creditRemaining,
  };
}

/** Reverses a customer credit application through the database RPC. */
export async function reverseCredit(
  payload: ReverseCreditPayload,
): Promise<CreditApplicationMutationResult> {
  if (!payload.application_id) {
    throw new Error("application_id is required");
  }

  const { data: rpcResult, error: rpcError } = await dataClient.rpc(
    "xan_reverse_customer_credit_application",
    { p_application_id: payload.application_id },
  );

  if (rpcError) {
    throw new Error(rpcError.message);
  }

  const rpcRecord = firstRecord(rpcResult);
  const reversalId =
    rpcRecord && typeof rpcRecord.reversal_id === "string" ? rpcRecord.reversal_id : null;
  const creditRemaining = rpcRecord ? toNumber(rpcRecord.credit_remaining) : null;

  if (!reversalId) {
    return cast<CreditApplicationMutationResult>(rpcResult ?? {});
  }

  const { data, error } = await dataClient
    .from("gl_credit_applications")
    .select(CREDIT_APPLICATION_SELECT)
    .eq("id", reversalId)
    .single();

  if (error || !data) {
    return cast<CreditApplicationMutationResult>(rpcResult ?? {});
  }

  return {
    ...cast<CreditApplication>(data),
    available_balance: creditRemaining,
    credit_remaining: creditRemaining,
  };
}
